import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Admin panel for closed-testing applications. Kept intentionally narrow —
// list + inline detail + accept/decline + read-only feedback view. No CSV,
// no bulk actions, no analytics.

const STATUS_LABEL = {
  new: "Новая",
  accepted: "Принята",
  declined: "Не отобрана",
  active_test: "Доступ открыт",
  completed_test: "Тест завершён",
  feedback_received: "Отзыв получен",
};

const STATUS_ORDER = ["new", "accepted", "active_test", "completed_test", "feedback_received", "declined"];

const REAL_ORDERS_LABEL = {
  regular: "Да, регулярно",
  sometimes: "Иногда",
  not_yet: "Пока нет",
};

const FEEDBACK_LABEL = { yes: "Да", no: "Нет" };

const PUBLICATION_LABEL = {
  with_name:     "Да, с моим именем",
  anonymous:     "Да, без имени",
  internal_only: "Нет, только для внутренней обратной связи",
};

function StatusBadge({ status }) {
  const color = {
    new:              { bg: "#F1E4CC", fg: "#5B4326" },
    accepted:         { bg: "#E4F1E7", fg: "#2F5B3D" },
    active_test:      { bg: "#E8ECFA", fg: "#2A3D6B" },
    completed_test:   { bg: "#F5E9DA", fg: "#6B4A2A" },
    feedback_received:{ bg: "#EAE1F2", fg: "#5A3D74" },
    declined:         { bg: "#F0E4E4", fg: "#7A3B3B" },
  }[status] || { bg: "#EFE6D5", fg: "#3E2E20" };
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] tracking-[0.06em]"
      style={{ background: color.bg, color: color.fg }}
      data-testid={`admin-mo-testing-status-${status}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

export default function MOTestingAdmin() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [actionPending, setActionPending] = useState(null); // id being accepted/declined

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/master-order-testing/applications");
      setApplications(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось загрузить заявки");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const arr = [...applications];
    arr.sort((a, b) => {
      const sa = STATUS_ORDER.indexOf(a.status);
      const sb = STATUS_ORDER.indexOf(b.status);
      if (sa !== sb) return sa - sb;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return arr;
  }, [applications]);

  const selected = useMemo(
    () => applications.find((a) => a.id === selectedId) || null,
    [applications, selectedId]
  );

  const doAction = async (id, action) => {
    setActionPending(id);
    try {
      const { data } = await api.post(`/admin/master-order-testing/applications/${id}/${action}`);
      setApplications((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success(action === "accept" ? "Заявка принята" : "Заявка отмечена как не отобранная");
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось выполнить действие");
    } finally {
      setActionPending(null);
    }
  };

  const copyTestimonial = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Отзыв скопирован");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  return (
    <div data-testid="admin-mo-testing-panel" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-espresso mb-1" data-testid="admin-mo-testing-title">
          Тестирование Мастера заказа
        </h2>
        <p className="text-sm text-muted-foreground">
          Заявки от кондитеров на участие в закрытом тестировании Мастера заказа.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="admin-mo-testing-loading">
          Загружаем заявки…
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl bg-sand/40 border border-sand text-espresso px-4 py-6 text-sm" data-testid="admin-mo-testing-empty">
          Пока нет ни одной заявки.
        </div>
      ) : (
        <div className="grid gap-3" data-testid="admin-mo-testing-list">
          {sorted.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-mo-testing-row-${a.id}`}
              className="rounded-2xl bg-white border border-sand/70 p-4 sm:p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[15px] font-medium text-espresso truncate">
                    {a.user_name_snapshot || "—"}
                  </div>
                  <div className="text-[12.5px] text-muted-foreground truncate">
                    {a.user_email || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={a.status} />
                </div>
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[13px] text-espresso">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Ссылка</div>
                  <a
                    href={a.social_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2 break-all"
                    style={{ color: "#5B4326" }}
                    data-testid={`admin-mo-testing-social-${a.id}`}
                  >
                    {a.social_url}
                  </a>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Реальные заказы</div>
                  <div>{REAL_ORDERS_LABEL[a.real_orders_status] || "—"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Готовность к отзыву</div>
                  <div>{FEEDBACK_LABEL[a.feedback_commitment] || "—"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Дата заявки</div>
                  <div>{formatDate(a.created_at)}</div>
                </div>
              </div>

              {a.simplify_work_text && (
                <div className="mt-3 text-[13px] text-espresso">
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Что упростить</div>
                  <div className="whitespace-pre-wrap">{a.simplify_work_text}</div>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {a.status === "new" && (
                  <>
                    <button
                      type="button"
                      onClick={() => doAction(a.id, "accept")}
                      disabled={actionPending === a.id}
                      className="h-9 px-4 rounded-full text-[12.5px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid={`admin-mo-testing-accept-${a.id}`}
                    >
                      Принять
                    </button>
                    <button
                      type="button"
                      onClick={() => doAction(a.id, "decline")}
                      disabled={actionPending === a.id}
                      className="h-9 px-4 rounded-full text-[12.5px] bg-transparent"
                      style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                      data-testid={`admin-mo-testing-decline-${a.id}`}
                    >
                      Не отбирать
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedId((cur) => (cur === a.id ? null : a.id))}
                  className="h-9 px-4 rounded-full text-[12.5px] bg-transparent"
                  style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #E4D3B0" }}
                  data-testid={`admin-mo-testing-toggle-${a.id}`}
                >
                  {selectedId === a.id ? "Свернуть" : "Открыть"}
                </button>
              </div>

              {selectedId === a.id && (
                <div
                  className="mt-4 pt-4 border-t border-sand/70 grid gap-3 text-[13px] text-espresso"
                  data-testid={`admin-mo-testing-detail-${a.id}`}
                >
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><span className="text-muted-foreground">Принята: </span>{formatDate(a.accepted_at)}</div>
                    <div><span className="text-muted-foreground">Не отобрана: </span>{formatDate(a.declined_at)}</div>
                    <div><span className="text-muted-foreground">Доступ открыт с: </span>{formatDate(a.test_access_started_at)}</div>
                    <div><span className="text-muted-foreground">Доступ до: </span>{formatDate(a.test_access_expires_at)}</div>
                    <div><span className="text-muted-foreground">Тест завершён: </span>{formatDate(a.completed_at)}</div>
                    <div><span className="text-muted-foreground">Отзыв отправлен: </span>{formatDate(a.feedback_submitted_at)}</div>
                  </div>

                  {a.feedback ? (
                    <div className="rounded-xl bg-sand/30 border border-sand/70 p-4" data-testid={`admin-mo-testing-feedback-${a.id}`}>
                      <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-1">Полный опыт</div>
                      <div className="whitespace-pre-wrap text-[13.5px] leading-[1.55] mb-3">
                        {a.feedback.experience_text}
                      </div>
                      {a.feedback.short_testimonial_text && (
                        <>
                          <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-1">Короткий отзыв</div>
                          <div
                            className="whitespace-pre-wrap text-[13.5px] leading-[1.55] mb-2 italic"
                            data-testid={`admin-mo-testing-feedback-testimonial-${a.id}`}
                          >
                            «{a.feedback.short_testimonial_text}»
                          </div>
                          <button
                            type="button"
                            onClick={() => copyTestimonial(a.feedback.short_testimonial_text)}
                            className="h-8 px-3 rounded-full text-[12px] bg-transparent"
                            style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                            data-testid={`admin-mo-testing-feedback-copy-${a.id}`}
                          >
                            Скопировать короткий отзыв
                          </button>
                        </>
                      )}
                      <div className="mt-3 text-[12.5px]">
                        <span className="text-muted-foreground">Разрешение на публикацию: </span>
                        <span data-testid={`admin-mo-testing-feedback-permission-${a.id}`}>
                          {PUBLICATION_LABEL[a.feedback.publication_permission] || a.feedback.publication_permission}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[12.5px] text-muted-foreground">
                      Отзыв пока не получен.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
